import { evaluateIsFoodArticle } from "@coworker/app/src/helpers/utils";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getLocationCategoryLabel } from "@coworker/apprestructured/src/task-management/helpers/locationCategory.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { useFilteredLocations } from "@coworker/apprestructured/src/task-management/services/useLocations";
import EmptyObject from "@coworker/apprestructured/src/task-management/types/EmptyObject";
import {
  Location,
  LocationChange,
} from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useTranslation } from "@coworker/locales";
import {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import PickupAndRefillLocation from "../../../PickupAndRefillLocation/PickupAndRefillLocation";
import styles from "./PickupAndRefillLocationCreatorFragment.module.css";

export type PickupAndRefillLocationCreatorFragmentConfigProps = EmptyObject;

type PickupAndRefillLocationCreatorFragmentProps = {
  configProps: PickupAndRefillLocationCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function PickupAndRefillLocationCreatorFragment({
  commonProps,
  ref,
}: PickupAndRefillLocationCreatorFragmentProps) {
  const { blockInput, task, onTaskChange, showValidation, taskType } =
    commonProps;
  const { t } = useTranslation();

  const pickupLocation = validateInitialDetailsFieldValue<Location>(
    getByPath(task, "details.pickup_location"),
    "details.pickup_location",
    taskType
  );

  const refillLocation = validateInitialDetailsFieldValue<Location>(
    getByPath(task, "details.refill_location"),
    "details.refill_location",
    taskType
  );

  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, "details.item"),
    "details.item",
    taskType
  );

  const itemNo =
    !isLoading(itemNoAndType) && itemNoAndType !== null ? itemNoAndType.no : "";
  const { data: itemInfo } = useItemInfo(itemNo);

  const isFoodArticle = evaluateIsFoodArticle(itemInfo?.paNo);

  const taskTypeId = taskType && !isLoading(taskType) ? taskType.id : undefined;

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [editingLocation, setEditingLocation] = useState<
    "pickup" | "refill" | null
  >(null);
  const handleTabChanged = useCallback(() => {}, []);

  const {
    isLoadingLocations,
    filteredLocations,
    totalAssignedSalesSpaceQuantity,
    homeLocation,
  } = useFilteredLocations(
    itemNoAndType,
    searchQuery,
    isFoodArticle,
    taskTypeId
  );

  useEffect(() => {
    if (!isLoading(pickupLocation) && !pickupLocation && !isLoadingLocations) {
      const preselectedLocation = homeLocation || (isFoodArticle ? "SFM" : "");
      if (preselectedLocation) {
        onTaskChange(
          setByPath("details.pickup_location", {
            slid: preselectedLocation,
            custom: "",
          })
        );
      }
    }
  }, [
    homeLocation,
    pickupLocation,
    onTaskChange,
    isLoadingLocations,
    isFoodArticle,
  ]);

  const handleConfirmLocation = ({ slid, customLocation }: LocationChange) => {
    if (!editingLocation) {
      return;
    }
    const field =
      editingLocation === "pickup"
        ? "details.pickup_location"
        : "details.refill_location";

    onTaskChange(
      setByPath(
        "details.home_location",
        homeLocation,
        setByPath(field, {
          slid: slid ?? "",
          custom: customLocation ?? "",
        })
      )
    );

    setEditingLocation(null);
  };

  const currentLocation =
    editingLocation === "pickup"
      ? pickupLocation
      : editingLocation === "refill"
        ? refillLocation
        : null;

  const currentLocationSlid =
    currentLocation && !isLoading(currentLocation) ? currentLocation.slid : "";
  const currentCustomLocation =
    currentLocation && !isLoading(currentLocation)
      ? currentLocation.custom
      : "";

  const pickupLocationText =
    !isLoading(pickupLocation) && pickupLocation
      ? pickupLocation.slid || pickupLocation.custom || ""
      : "";
  const refillLocationText =
    !isLoading(refillLocation) && refillLocation
      ? refillLocation.slid || refillLocation.custom || ""
      : "";

  const pickupLocationCategory =
    !isLoading(pickupLocation) && pickupLocation && !isLoadingLocations
      ? getLocationCategoryLabel(pickupLocation, filteredLocations, t)
      : undefined;

  const refillLocationCategory =
    !isLoading(refillLocation) && refillLocation && !isLoadingLocations
      ? getLocationCategoryLabel(refillLocation, filteredLocations, t)
      : undefined;

  const isPickUpAndRefillLocationSame =
    pickupLocationText.length > 0 && pickupLocationText === refillLocationText;

  const hasPickupLocation = pickupLocationText.length > 0;
  const hasRefillLocation = refillLocationText.length > 0;
  const hasRequiredLocations = hasPickupLocation && hasRefillLocation;
  const hasMissingLocationError = !hasRequiredLocations;

  const valid =
    !isLoading(pickupLocation) &&
    !isLoading(refillLocation) &&
    pickupLocation !== null &&
    refillLocation !== null &&
    hasRequiredLocations &&
    !isPickUpAndRefillLocationSame;

  useImperativeHandle(ref, () => ({ valid }));

  if (
    isLoading(pickupLocation) ||
    isLoading(refillLocation) ||
    isLoading(itemNoAndType) ||
    (!pickupLocation && isLoadingLocations)
  ) {
    return <LoadingCreatorFragment />;
  }

  return (
    <div className={styles["root"]}>
      <PickupAndRefillLocation
        pickupLocation={pickupLocationText}
        refillLocation={refillLocationText}
        pickupLocationCategory={pickupLocationCategory}
        refillLocationCategory={refillLocationCategory}
        isPickUpAndRefillLocationSame={isPickUpAndRefillLocationSame}
        showValidation={showValidation && hasMissingLocationError}
        onPickupClick={() => setEditingLocation("pickup")}
        onRefillClick={() => setEditingLocation("refill")}
        disabled={blockInput}
        locationModalProps={{
          visible: editingLocation !== null,
          onClose: () => setEditingLocation(null),
          onTabChanged: handleTabChanged,
          itemNoAndType,
          slid: currentLocationSlid,
          customLocation: currentCustomLocation,
          onConfirmClick: handleConfirmLocation,
          isLoadingLocations,
          filteredLocations,
          totalAssignedSalesSpaceQuantity,
          searchQuery,
          setSearchQuery,
          withCustomLocations: isFoodArticle,
          hideCustomLocationTab: !isFoodArticle,
        }}
      />
    </div>
  );
}

export default PickupAndRefillLocationCreatorFragment;
